import { esc, kr, marginPill, nf1, num, pct } from '../calc/format.js';
import { change } from '../calc/periods.js';
import { withCharts } from './chartloader.js';
import { el, emptyRow } from './dom.js';
import { downloadXlsx, selectionSlug, slug } from './export.js';
import { state } from './state.js';

/* ---- Utveckling över tid ---- */

// [etikett, formaterare, enhetslöst mått, ökning är negativ]
export const METRICS = {
  revenue:        ['Omsättning',             kr,   false, false],
  profit:         ['Bruttovinst',            kr,   false, false],
  margin:         ['Bruttomarginal',         pct,  true,  false],
  tx:             ['Antal köp',              num,  false, false],
  aov:            ['Snittköp',               kr,   false, false],
  itemsPerTx:     ['Artiklar per köp',       v => nf1.format(v).replace('.',','), false, false],
  avgUnitPrice:   ['Snittpris per artikel',  kr,   false, false],
  multiLineShare: ['Köp med flera artiklar', pct,  true,  false],
  returnShare:    ['Returandel',             pct,  true,  true],
  discountShare:  ['Rabattgrad',             pct,  true,  true],
  emailShare:     ['Andel e-postkvitton',    pct,  true,  false],
  trashShare:     ['Andel bortvalda kvitton', pct,  true,  false],
  custShare:      ['Köp med kund',           pct,  true,  false],
  custRevenueShare: ['Försäljning med kund', pct,  true,  false],
  custAov:        ['Snittköp med kund',      kr,   false, false],
  bonusCount:     ['Inlösta bonuscheckar',   num,  false, false]
};

export const GRAIN_WORD = {day:'dag', week:'vecka', month:'månad'};
const GRAIN_PLURAL = {day:'dagar', week:'veckor', month:'månader'};

export function renderTrend(){
  const series = state.series;
  const key = state.metric;
  const [label, fmt, isPoints, invert] = METRICS[key];
  const grain = state.grain;

  el('trendTitle').textContent = `${label} per ${GRAIN_WORD[grain]}`;


  // Vad serien omfattar, när det inte är hela filens alla dagar och timmar
  const scope = [state.trendSpan === 'period' ? 'Vald period' : '', state.dayTime].filter(Boolean).join(' · ');
  const withScope = t => scope ? `${scope} · ${t}` : t;

  if(!series.length){
    el('trendSub').textContent = withScope('Ingen försäljning i urvalet');
    el('trendCount').textContent = '';
    el('tTrend').innerHTML = emptyRow(7,'Inga kvitton i urvalet','Bredda urvalet av period, veckodagar, klockslag, butik, kassa eller kassör.');
    withCharts(el('cTrendMain'), c => c.clearTrendChart(el('cTrendMain')));
    return;
  }

  const values = series.map(p => p.stats[key]);

  // Sammanfattningen ska bygga på kompletta perioder. En avslutande halv månad
  // skulle annars se ut som ett kraftigt fall.
  const full = series.filter(p => !p.partial);
  const base = full.length >= 2 ? full : series;
  const first = base[0], last = base[base.length-1];

  let devText;
  if(base.length < 2){
    devText = `${fmt(first.stats[key])} i ${first.label}`;
  }else if(isPoints){
    const d = last.stats[key] - first.stats[key];
    devText = `${fmt(first.stats[key])} i ${first.label} till ${fmt(last.stats[key])} i ${last.label}, ${d >= 0 ? '+' : '−'}${nf1.format(Math.abs(d)).replace('.',',')} procentenheter`;
  }else{
    const c = change(last.stats[key], first.stats[key]);
    devText = `${fmt(first.stats[key])} i ${first.label} till ${fmt(last.stats[key])} i ${last.label}` +
      (c === null ? '' : `, ${c >= 0 ? '+' : '−'}${nf1.format(Math.abs(c)).replace('.',',')} %`);
  }
  const skipped = series.length - base.length;
  if(skipped > 0){
    devText += ` · ${skipped === 1 ? 'En ofullständig period' : skipped + ' ofullständiga perioder'} utelämnad${skipped === 1 ? '' : 'e'} ur jämförelsen och nedtonad${skipped === 1 ? '' : 'e'} i diagrammet`;
  }
  el('trendSub').textContent = withScope(devText);

  // Belopp och antal ritas som staplar, andelar och snittvärden som linje
  const asBars = key === 'revenue' || key === 'profit' || key === 'tx' || key === 'bonusCount';
  const unit = isPoints ? 'pct' : (key === 'tx' || key === 'bonusCount') ? 'num' : 'kr';

  // Glidande medelvärde först när serien är lång nog att brusa
  let avg = null;
  if(series.length >= 10){
    const win = grain === 'day' ? 7 : 3;
    avg = values.map((_,i) => {
      const slice = values.slice(Math.max(0, i - (win-1)), i+1);
      return slice.reduce((a,b)=>a+b,0) / slice.length;
    });
  }
  // En ofullständig period har lägre värde bara för att den är kortare.
  // Den tonas ned så att den inte läses som ett verkligt fall.
  const props = {
    points: series.map((p, i) => ({label: p.label, value: values[i], avg: avg ? avg[i] : null, partial: p.partial})),
    kind: asBars ? 'bar' : 'line', unit, label,
    // Dagar utan försäljning finns inte i serien, så snittet är över sju
    // dagar med försäljning — med bara fredagar valda sju fredagar
    avgLabel: grain === 'day' ? 'Snitt över 7 dagar med försäljning' : `Snitt över 3 ${GRAIN_PLURAL[grain]}`
  };
  withCharts(el('cTrendMain'), c => c.drawTrendChart(el('cTrendMain'), props));

  renderTrendTable(series, invert);
}

export function renderTrendTable(series, invert){
  el('trendCount').textContent = `${num(series.length)} ${series.length === 1 ? 'period' : 'perioder'}`;

  let html = `<thead><tr><th>Period</th><th class="num">Omsättning</th><th class="num">Förändring</th>
    <th class="num">Antal köp</th><th class="num">Snittköp</th><th class="num">Bruttovinst</th>
    <th class="num">Marginal</th></tr></thead><tbody>`;

  // Nyast först: det är där kunden vill börja läsa
  for(let i = series.length - 1; i >= 0; i--){
    const p = series[i], st = p.stats;
    const before = i > 0 ? series[i-1].stats.revenue : null;
    const c = before === null ? null : change(st.revenue, before);

    let cell = '<span style="color:var(--ink-soft)">–</span>';
    if(p.partial){
      // Perioden är inte komplett, så förändringstalet vore missvisande
      cell = '<span style="color:var(--ink-soft)">ofullständig</span>';
    }else if(c !== null){
      if(Math.abs(c) < 0.5){
        cell = '<span style="color:var(--ink-soft)">oförändrat</span>';
      }else{
        const dir = c > 0 ? 'up' : 'down';
        cell = `<span class="delta ${dir} ${c > 0 ? 'good' : 'bad'}">${esc((c > 0 ? '+' : '−') + nf1.format(Math.abs(c)).replace('.',',') + ' %')}</span>`;
      }
    }

    html += `<tr${p.partial ? ' class="muted"' : ''}>
      <td class="name">${esc(p.label)}${p.partial ? '<small>delvis period i filen</small>' : ''}</td>
      <td class="num">${esc(kr(st.revenue))}</td>
      <td class="num">${cell}</td>
      <td class="num">${esc(num(st.tx))}</td>
      <td class="num">${esc(kr(st.aov))}</td>
      <td class="num">${state.hasCost ? esc(kr(st.profit)) : '–'}</td>
      <td class="num">${state.hasCost && st.revenueEx ? marginPill(st.margin) : '–'}</td>
    </tr>`;
  }

  const anyPartial = series.some(p => p.partial);
  html += '</tbody>';
  if(anyPartial){
    html += `<tfoot><tr><td colspan="7" style="color:var(--ink-soft);font-size:13px;padding-top:14px;border-bottom:none">
      Perioder märkta ofullständig finns bara delvis i filen. Beloppen stämmer,
      men de går inte att jämföra rakt av med hela perioder.
      </td></tr></tfoot>`;
  }
  el('tTrend').innerHTML = html;
}

export function exportTrend(){
  const rows = state.series.map(p => {
    const st = p.stats;
    // Utan inköpspris i filen finns ingen bruttovinst att exportera
    return [p.label, st.revenue, st.revenueEx, state.hasCost ? st.profit : null,
            state.hasCost && st.revenueEx ? st.margin : null,
            st.tx, st.aov, st.itemsPerTx, st.returnShare, st.discountShare,
            p.partial ? 'Ofullständig period' : ''];
  });
  return downloadXlsx(`easycashier-utveckling-per-${slug(GRAIN_WORD[state.grain])}${selectionSlug()}`, {
    sheet: 'Utveckling per ' + GRAIN_WORD[state.grain],
    columns: [{h:'Period'}, {h:'Omsättning inkl. moms', t:'num'}, {h:'Omsättning exkl. moms', t:'num'},
      {h:'Bruttovinst', t:'num'}, {h:'Marginal %', t:'pct'}, {h:'Antal köp', t:'int'}, {h:'Snittköp', t:'num'},
      {h:'Artiklar per köp', t:'num'}, {h:'Returandel %', t:'pct'}, {h:'Rabattgrad %', t:'pct'}, {h:'Anmärkning'}],
    rows
  });
}
